/*
XOL-67 (RTA redesign v2.1, V1): the words and the bodies of the RTA option layer (`AcquireDetails`, the Options table), in
pure functions. No Vue, no store, no axios: the component passes who is looking and what the organisations are called, and
the specs drive them with plain objects.

  optionBar(...)            the option header's STATUS LINE: a sentence, no buttons (Propose, Accept, Decline and Withdraw are gone)
  declineReason(...)        the Tower Co's reason on an option it declined in Step 4 (ReadOnlyRemarks label + stamp)
  submitRow(...)            the Review section's Submit review button, its hint and its stamp
  reviewOpenLabel(...)      the label of the sticky review footer's opener (decision 190): Begin / Edit / Change review
  reviewCallToAction(...)   the one line in that footer that says what to do (Carlos follow-up, 2026-10-29): '' once submitted and unchanged
  approvingNote(...)        why Approved is unavailable while another option is approved
  tcResponseView(...)       the value of record of the Review section (the submitted review) and its "Changed since submitted" alert
  reviewNote(...)           the "Please Note" over the Review Remarks, by who reads them
  reviewReadOnly(...)       the Review section when the viewer cannot edit it (Searching, Finalize, closed, view-only)
  contentBody(...)          the body of POST / PUT /candidate: the caller's allow-list, nothing server-owned
  contentChanges(...)       the keys of that body that differ from what the server last took (the option's auto-save, decision 125)
  detailsLockNote(...)      the line over the details panes once the option is locked (decision 126)
  coordinateRow(...)        the Request / Candidate / Distance cells, "Not set" for a missing point (no NaN, no 0 / 0 / 0)
  requestPoint(rta)         the request's point { lat, lng } or null; requestSite(rta) the request in the shape of a site (the map centre, the
                            stand-in "site" of the option screens); optionDefaultsFromRequest(rta) what a new option starts with (decision 158)
  optionMarkerLook(n)       an option's pin: its label "Option N", its icon and its colour, all from the option number (decisions 144, 155)
  requestMarkerLook()       the request's crosshairs, no number (decision 156)
  accessPinFor(n)           the access marker's road pin, one per option (decisions 147, 151, 155)
  accessMarkerLook(n)       { icon, label } of an option's access marker (the UX spec's name; extraMarkerLook('access', n))
  extraMarkerLook(type, n)  an access / power / building marker: { icon, label "Access · Option 2" } (decision 147)
  extraMarkerRows(...)      the list under the add buttons: one row per extra marker, grouped access, power, building and numbered within its type ("Access 1 · 85 m from option 2")
  markerDropPoint(...)      where a new extra marker lands: 48 px east (access), west (power) or south (building) of the option's pin
  newExtraMarker(...)       the entry a + Access / + Power / + Building press adds to `markers`; extraMarkerHint, addMarkerLabel, extraMarkerName: its copy
  markersForOptionMap(...)  the pins of ONE option's map: the request crosshairs, its own pin, its own extras (decisions 147, 156)
  markersForStepMap(...)    the pins of the step Map: the crosshairs, every option's pin and extras, all read-only (decisions 147, 156)
  markersForRequestMap(...) the pins of the Step 1 map: the crosshairs (draggable for who may edit the request) and every option's pin (delta 2.5)
  coordinate, hasPoint, mapCentre, markersCentre, MALAYSIA_CENTRE   the map helpers (they live here; lib/rtaNominal re-exports them)
  responseBody(...)         the body of PUT /candidate/{id}/response
  sentence(kind, ctx, p)    the toast sentence of a command
  deleteBlockedCopy(reason, ctx)  why the client's Delete of its own option is disabled (the tooltip, and the button's name)
  confirmCopy(kind, ...)    the title, body and buttons of a confirm / the alert of an add in Review

The copy is the v2 UX spec (docs/plans/2026-10-27-xol-67-rta-v2.md, section 2.3, 2.4, 4.5, 4.6) and the mock beside it, with the v2.1 step names
(Searching, Client Review; Back to Searching) and step numbers (Tower Co Decision is Step 4, Finalize Step 5). The bodies
are the API plan's: DEV-10 (three allow-lists, as API-6 changes them), DEV-11 (an option PUT never carries the live review or any
server-owned key), DEV-26 (idempotencyKey). The Decline and Reject RTA… dialogs are RtaDialog descriptors and live in rtaDecision.

ctx { telco, towerCo, fmtDate, fmtDateTime, liveCount, mailPossible, mailOff, tcClient, side }
  telco         the client's name (for a Tower Co client, that Tower Co); 'the client' without it
  towerCo       the Tower Co's name
  side          'tc' when the caller is a Tower Co user recording the review for the client (decisions 76, 77, 98),
                'client' (or absent) for the client or a Tower Co user answering a Telco proposal
  fmtDate       a function, the date as the app shows it (the raw value without it)
  fmtDateTime   a function, the date and time as the app shows them
  liveCount     the RTA's live options (the limit of 5)
  mailPossible  TRUE only when it is known: a Telco client, Send E-mails on, an open RTA (hints that say someone "is e-mailed").
                Anything unknown (the option row's compact `rta` has no `emails`) is not true, and the hints are not shown
  mailOff       TRUE only when Send E-mails is known to be off for this RTA
  tcClient      the client is a Tower Co: no e-mail is ever sent, and there is no Please Note
  answering     TRUE when the Tower Co submits a review in its own right because the Telco proposed to it (`proposedTo` 'towerco'):
                its Submit then e-mails the client, and the hint says so

ES5, no optional chaining or nullish coalescing (Babel 6).
------------------------------------------------------------------------ */

import { hasRemarksText } from '@/lib/remarksText'
import {
  isTerminal, isTowerCoUser, canEditReview, localResponseChanged, responseChanged, optionDetailsLock,
  canEditOptionDetails, canEditRequestField, serverResponseChanged, proposedToOf, rtaTitle, referenceOf
} from '@/lib/rtaStage'

function present (v) {
  return v !== undefined && v !== null && v !== ''
}

function has (obj, key) {
  return Object.prototype.hasOwnProperty.call(obj, key)
}

function fmt (fn, value) {
  if (!present(value)) {
    return ''
  }

  return typeof fn === 'function' ? String(fn(value)) : String(value)
}

// " on 26 Oct 2026", or nothing when the date is not known.
function on (text) {
  return text ? ' on ' + text : ''
}

/* ------------------------------------------------------------------------
   The status line (UX spec 4.5)
------------------------------------------------------------------------ */

// The organisation a party key names, or '' when the key is empty. Never guessed: a legacy row has no party (API DEV-53).
function partyName (party, ctx) {
  if (party === 'towerco') {
    return ctx.towerCo || 'the Tower Co'
  }

  if (party === 'owner') {
    return ctx.telco || 'the client'
  }

  return ''
}

// The side that holds the option's review: the one the options were proposed to (the client when nothing says).
function reviewerName (rta, ctx) {
  return proposedToOf(rta) === 'towerco' ? partyName('towerco', ctx) : partyName('owner', ctx)
}

/**
 * "Approved by Telco Beta on 26 Oct 2026." / "Declined by ... ": who submitted the review, from `submitted_by_party`.
 * The Tower Co recording the client's answer (the options were proposed to the client) says so: "Approved by User A (TowerCo Alpha)
 * for Telco Beta on ...". The Tower Co answering a Telco proposal, or any review of a Tower Co-client RTA, is the Tower Co's own.
 */
function reviewedLine (verb, option, rta, ctx) {
  var tc = ctx.towerCo || 'the Tower Co'
  var telco = ctx.telco || 'the client'
  var when = on(fmt(ctx.fmtDate, option.submitted_at || option.client_status_at))
  var by = ''

  if (option.submitted_by_party === 'owner') {
    by = ' by ' + telco
  } else if (option.submitted_by_party === 'towerco') {
    var name = option.submitted_by ? String(option.submitted_by) + ' (' + tc + ')' : tc

    by = ctx.tcClient === true || proposedToOf(rta) === 'towerco' ? ' by ' + tc : ' by ' + name + ' for ' + telco
  }

  return verb + by + when + '.'
}

/**
 * The option header's status line: { side, type (el-alert), title }. No buttons: v2 has no per-option Propose, Accept, Decline or
 * Withdraw (the RTA proposes as one, Back to Searching takes it back, the Review section answers). `side` is the viewer's: 'tc'
 * for any Tower Co user, 'client' for everyone else.
 *
 *   Draft             "Draft. It is proposed with the RTA's other Draft options."
 *   Review            "In review. Set the Review Status at the bottom of this option and submit it." (the viewer may review)
 *                     "In review with {Telco} since {date}." (the viewer waits: view-only, or frozen)
 *   Approved          "Approved by {Telco} on {date}." / "Approved by {name} ({Tower Co}) for {Telco} on {date}."
 *   Declined          "Declined by {Telco} on {date}." / "Declined by {Tower Co} in Step 4 on {date}." (a Step 4 Decline)
 *   Final Approved    "Selected: the site was created from this option."
 *   Not Selected      "Not selected."
 */
export function optionBar (option, rta, org, profile, ctx) {
  ctx = ctx || {}

  var side = isTowerCoUser(profile) ? 'tc' : 'client'
  var status = option ? option.optionStatus : ''
  var type = 'info'
  var title = ''

  switch (status) {
    case 'Draft':
      title = 'Draft. It is proposed with the RTA\'s other Draft options.'
      break

    case 'Review':
      if (canEditReview(option, rta, org, profile)) {
        type = 'warning'
        title = 'In review. Set the Review Status at the bottom of this option and submit it.'
      } else {
        var since = fmt(ctx.fmtDate, option.proposed_at)

        title = 'In review with ' + reviewerName(rta, ctx) + (since ? ' since ' + since : '') + '.'
      }
      break

    case 'Approved':
      type = 'success'
      title = reviewedLine('Approved', option, rta, ctx)
      break

    case 'Declined':
      // A Step 4 Decline writes `declineReason` (I-6); a Declined review has none.
      title = present(option.declineReason)
        ? 'Declined by ' + (ctx.towerCo || 'the Tower Co') + ' in Step 4' + on(fmt(ctx.fmtDate, option.declined_at || option.submitted_at)) + '.'
        : reviewedLine('Declined', option, rta, ctx)
      break

    case 'Final Approved':
      type = 'success'
      title = 'Selected: the site was created from this option.'
      break

    case 'Not Selected':
      title = 'Not selected.'
      break

    default:
      title = ''
  }

  return { side: side, type: type, title: title }
}

/**
 * The Tower Co's reason on an option it declined in Step 4 (UX spec 2.4), for both sides: { label, html, stamp }, or null when the
 * option is not Declined or has no reason. The client's stamp has no "by {name}".
 */
export function declineReason (option, side, ctx) {
  ctx = ctx || {}

  if (!option || option.optionStatus !== 'Declined' || !present(option.declineReason)) {
    return null
  }

  var when = fmt(ctx.fmtDateTime, option.declined_at)
  var who = side === 'tc' && option.declined_by ? ' by ' + option.declined_by + ' (' + (ctx.towerCo || 'the Tower Co') + ')' : ''

  return {
    label: 'Reason for declining',
    html: option.declineReason,
    stamp: when || who ? 'Declined' + who + on(when) : null
  }
}

/* ------------------------------------------------------------------------
   The Review section (UX spec 2.4.1, 4.5; decisions 43, 44, 54, 76)
------------------------------------------------------------------------ */

/**
 * "Submitted by {name} ({org}) on {date} {time}" (decisions 54, 76), or '' when nothing was submitted. `{org}` is
 * read from `submitted_by_party` ('towerco' is the Tower Co, 'owner' the client). Without the key the text is the
 * old "Submitted by {name} on ..."; with a party and no name it is "Submitted by {org} on ..."; with neither,
 * "Submitted on ...".
 */
export function submittedStamp (option, ctx) {
  ctx = ctx || {}

  if (!option || !present(option.submitted_at)) {
    return ''
  }

  var name = option.submitted_by ? String(option.submitted_by) : ''
  var org = partyName(option.submitted_by_party, ctx)
  var by = ''

  if (name) {
    by = ' by ' + name + (org ? ' (' + org + ')' : '')
  } else if (org) {
    by = ' by ' + org
  }

  return 'Submitted' + by + on(fmt(ctx.fmtDateTime, option.submitted_at))
}

/**
 * The Submit row: { label, icon, disabled, loading, hint, hintKind, hintIcon, stamp, state }.
 *
 *   never submitted     "Submit review", enabled once there is something to submit
 *   submitting          loading, "Submitting…"
 *   submitted unchanged disabled, fa-check "Review submitted", the stamp
 *   changed             "Submit review", the fa-pencil-alt hint, the stamp
 *
 * The hint (UX spec 4.5):
 *   the client, mail on                      "Submitting e-mails {Tower Co}."                                       ('mail')
 *   the Tower Co recording for the client    "Records this review on behalf of {Telco}. Nobody is e-mailed."        ('behalf')
 *   the Tower Co answering a Telco proposal  "Submitting e-mails {Telco}."                                          ('mail')
 *   mail off                                 "Send E-mails is off for this RTA, so nobody is e-mailed."             ('off')
 *   a Tower Co client                        none
 *   changed since submitted                  "Changed since you submitted. Submit again to send it." (the Tower Co: "... Submit again to record it.")
 *
 * The button never carries a date: the stamp does (decision 54). `hintKind` ('mail' | 'behalf' | 'off' | 'changed' | '')
 * says which hint it is, so a spec can assert it without the copy. `ctx.side` is 'tc' when the Tower Co records the review for
 * the client and 'client' when the caller answers in its own right; `ctx.mailPossible` is true only when it is known that an
 * e-mail would go (a mail hint is never said on a guess).
 *
 * @param {Object} live  { status, remarks } as on screen
 * @param {Boolean} busy a Submit is in flight
 */
export function submitRow (option, live, busy, ctx) {
  ctx = ctx || {}

  var tc = ctx.towerCo || 'the Tower Co'
  var telco = ctx.telco || 'the client'
  var byTc = ctx.side === 'tc'
  var stamp = submittedStamp(option, ctx)
  var submitted = present(option && option.submitted_at)
  var changed = localResponseChanged(option, live) || serverResponseChanged(option)

  if (busy) {
    return { state: 'submitting', label: 'Submitting…', icon: 'fa fa-paper-plane', disabled: true, loading: true, hint: '', hintKind: '', hintIcon: '', stamp: stamp }
  }

  if (submitted && !changed) {
    return { state: 'submitted', label: 'Review submitted', icon: 'fa fa-check', disabled: true, loading: false, hint: '', hintKind: '', hintIcon: '', stamp: stamp }
  }

  var row = { state: 'never', label: 'Submit review', icon: 'fa fa-paper-plane', disabled: !changed, loading: false, hint: '', hintKind: '', hintIcon: '', stamp: stamp }

  if (submitted) {
    row.state = 'changed'
    row.hintKind = 'changed'
    row.hintIcon = 'fa-pencil-alt'
    row.hint = byTc
      ? 'Changed since it was submitted. Submit again to record it.'
      : 'Changed since you submitted. Submit again to send it.'
  } else if (ctx.tcClient === true) {
    // A Tower Co client: nothing is ever e-mailed, so there is nothing to say.
    row.hint = ''
  } else if (byTc) {
    row.hintKind = 'behalf'
    row.hint = 'Records this review on behalf of ' + telco + '. Nobody is e-mailed.'
  } else if (ctx.mailOff === true) {
    row.hintKind = 'off'
    row.hint = 'Send E-mails is off for this RTA, so nobody is e-mailed.'
  } else if (ctx.mailPossible === true) {
    row.hintKind = 'mail'
    row.hint = 'Submitting e-mails ' + (ctx.answering === true ? telco : tc) + '.'
  }

  return row
}

/**
 * Decision 190: the label of the opener in the option's sticky review footer, from the Submit row and the live review on screen.
 *   never submitted, live status Pending and no remarks   "Begin review"
 *   never submitted, anything typed or chosen              "Edit review"
 *   changed since submitted / submitting                   "Edit review"
 *   submitted and unchanged                                "Change review"
 *
 * @param {Object} submitRow rtaOption.submitRow(...): only its `state` is read
 * @param {Object} live      { status, remarks } as on screen
 */
export function reviewOpenLabel (submitRow, live) {
  var state = submitRow && submitRow.state ? submitRow.state : 'never'
  var l = live || {}

  if (state === 'submitted') {
    return 'Change review'
  }

  if (state === 'never' && (!present(l.status) || l.status === 'Pending') && !hasRemarksText(l.remarks)) {
    return 'Begin review'
  }

  return 'Edit review'
}

/**
 * Carlos follow-up to decision 190 (2026-10-29): the one line in the review footer that says what to do, for a viewer who can review. '' when there is
 * nothing to do (submitted and unchanged, or a Submit in flight); the caller passes '' for a viewer who cannot review.
 *   never submitted, the client or a Tower Co user answering    "Your review is needed: set Approved or Declined, then submit it."
 *   never submitted, a Tower Co user recording for the client   "Record {Telco}'s review: set Approved or Declined, then submit it."
 *   changed since submitted                                      "Submit it again to send the change." (beside the "Changed since submitted" chip)
 *
 * @param {Object} submitRow rtaOption.submitRow(...): only its `state` is read
 * @param {Object} ctx       { recording: a Tower Co user records the review for the client, telco }
 */
export function reviewCallToAction (submitRow, ctx) {
  var state = submitRow && submitRow.state ? submitRow.state : ''
  var c = ctx || {}

  if (state === 'changed') {
    return 'Submit it again to send the change.'
  }

  if (state !== 'never') {
    return ''
  }

  return c.recording === true
    ? 'Record ' + (c.telco || 'the client') + '\'s review: set Approved or Declined, then submit it.'
    : 'Your review is needed: set Approved or Declined, then submit it.'
}

/**
 * "Option 2 is approved. Only one option can be approved: change option 2's review first." under the Review Status select while
 * another option holds the approval (I-10, M-15). Empty when none does. Pass rtaStage.otherApprovedNo(option, rta).
 */
export function approvingNote (otherApprovedNo) {
  if (!otherApprovedNo) {
    return ''
  }

  return 'Option ' + otherApprovedNo + ' is approved. Only one option can be approved: change option ' + otherApprovedNo + '\'s review first.'
}

/**
 * The value of record of the Review section (UX spec 2.4.1): the SUBMITTED review. { submitted: 'Approved' | 'Declined' | 'Pending'
 * | null, remarks, stamp, changed, alert }. `alert` is the "Changed since submitted" text, present
 * only when the server says `responseChanged`. It is the Review section's alert for any Tower Co user beside the value of record, the form's
 * reviewer included (the mock's text: "The submitted review above is still the one of record.").
 *
 * Both name who made the unsubmitted change from `client_status_by_party` (API DEV-36): the Tower Co (with the
 * name in `client_status_by` when there is one) for 'towerco', the client for 'owner' or a missing key.
 */
export function tcResponseView (option, ctx) {
  ctx = ctx || {}

  var tc = ctx.towerCo || 'the Tower Co'
  var telco = ctx.telco || 'the client'
  var submitted = !!option && present(option.submitted_at)
  var changed = responseChanged(option)
  var byTc = !!option && option.client_status_by_party === 'towerco'
  var who = byTc ? (option.client_status_by ? option.client_status_by + ' (' + tc + ')' : tc) : telco

  return {
    submitted: submitted ? (option.submittedStatus || 'Pending') : null,
    remarks: submitted ? option.submittedRemarks : null,
    stamp: submittedStamp(option, ctx),
    changed: changed,
    alert: changed
      ? {
        title: 'Changed since submitted',
        description: who + ' changed this review and hasn\'t submitted it. The submitted review above is still the one of record.'
      }
      : null
  }
}

/**
 * "Please Note" over the Review Remarks (UX spec 2.4.1): who reads them. '' for a Tower Co client (nobody to tell, no alert).
 *   the client                          "These remarks will be visible to {Tower Co} and will be sent to {Tower Co} when you submit."
 *   a Tower Co user, recording          "These remarks are visible to {Telco}."
 *   a Tower Co user, answering a Telco  "These remarks are visible to {Telco} and are sent to {Telco} when you submit."
 *
 * @param {Object} v { viewerTc (the viewer is a Tower Co user), answering (the options were proposed to the Tower Co), tcClient }
 */
export function reviewNote (v, ctx) {
  v = v || {}
  ctx = ctx || {}

  var tc = ctx.towerCo || 'the Tower Co'
  var telco = ctx.telco || 'the client'

  if (v.tcClient === true) {
    return ''
  }

  if (!v.viewerTc) {
    return 'These remarks will be visible to ' + tc + ' and will be sent to ' + tc + ' when you submit.'
  }

  return 'These remarks are visible to ' + telco + (v.answering === true ? ' and are sent to ' + telco + ' when you submit.' : '.')
}

/**
 * The Review section when the viewer cannot edit it (UX spec 2.4.1 "Read-only states"): { status, remarks, stamp, note, noteIcon }.
 *   Searching   "Not reviewed" (or the last submitted value), "Reviews open when the options are proposed."
 *   Finalize    the submitted value and remarks, "Frozen while {Tower Co} finalizes the site." with a lock
 *   closed      the submitted value and remarks, no note
 * Anywhere else (a view-only Tower Co user in Client Review or Client Approved, a client on a terminal option) it is the value of record with no
 * note. `status` is the submitted review, else "Not reviewed".
 */
export function reviewReadOnly (option, rta, ctx) {
  ctx = ctx || {}

  var tc = ctx.towerCo || 'the Tower Co'
  var submitted = !!option && present(option.submitted_at)
  var note = ''
  var noteIcon = ''

  if (rta && !isTerminal(rta)) {
    if (rta.status === 'Finalize') {
      note = 'Frozen while ' + tc + ' finalizes the site.'
      noteIcon = 'fa-lock'
    } else if (rta.status === 'Searching') {
      note = 'Reviews open when the options are proposed.'
    }
  }

  return {
    status: submitted ? (option.submittedStatus || 'Pending') : 'Not reviewed',
    remarks: submitted && present(option.submittedRemarks) ? option.submittedRemarks : null,
    stamp: submittedStamp(option, ctx),
    note: note,
    noteIcon: noteIcon
  }
}

/* ------------------------------------------------------------------------
   The extra markers of an option: the list, the add, the copy (decisions 147, 149; UX spec 2.4.2, 4.5)
------------------------------------------------------------------------ */

// What each type is told to do until its marker is first dragged (UX spec 4.5, "Row, new until its first drag").
var DRAG_TELLS = {
  access: 'Drag it to where the site is reached from the road.',
  power: 'Drag it to the nearest power supply.',
  building: 'Drag it to the nearest building.'
}

/** The label of a marker type on a button and a row: "Access", "Power", "Building". */
export function extraMarkerName (type, k) {
  return has(EXTRA_LABELS, String(type)) ? EXTRA_LABELS[String(type)] + (Number(k) > 0 ? ' ' + Number(k) : '') : ''
}

/** "Add an access marker to option 2" / "Add a power marker ..." / "Add a building marker ..." (the button's aria-label). */
export function addMarkerLabel (type, optionNo) {
  return 'Add ' + (type === 'access' ? 'an' : 'a') + ' ' + String(type) + ' marker to option ' + optionNo
}

/**
 * The hint over the extra-markers list (UX spec 4.5; decision 179 moved the add buttons onto the map; decision 180 adds them at the map's centre):
 * "Markers are added at the centre of the map, zoomed in to building level. Drag one into place; it saves on its own." or, with no point yet,
 * "Place option {n}'s pin first, then add markers."
 */
export function extraMarkerHint (hasOptionPoint, optionNo, adding) {
  if (!hasOptionPoint) {
    return 'Place option ' + optionNo + '\'s pin first, then add markers.'
  }

  // Decision 187: in the New Option form a marker goes with the option when it is saved, not on its own.
  return adding
    ? 'Markers are added at the centre of the map, zoomed in to building level. Drag one into place; it is saved with the option.'
    : 'Markers are added at the centre of the map, zoomed in to building level. Drag one into place; it saves on its own.'
}

/**
 * Decision 188: the Markers table under an RTA map (the Step 2 / 3 map, the option map, the New Option form): one row per pin the map draws, in the
 * map's order: [{ key, kind, type, optionId, optionNo, markerIndex, name, icon, lat, lng, metres, from, deletable }].
 *   - `key` is the pin's own key, so a row and its pin highlight each other (the map's `highlight`, its `pin-hover`).
 *   - the requested point has no distance; an option's pin is measured from the requested point; an extra marker from its own option's pin.
 *   - `name`: "Requested point", "Option 2", "Access 1" (`withOption`: "Access 1 · option 2", for a map of several options).
 *   - `deletable`: an extra marker, when `canDelete` (the pins and the crosshairs are never deleted here).
 */
export function markerTableRows (pins, opts) {
  var o = opts || {}
  var list = Array.isArray(pins) ? pins.filter(function (p) { return !!p && !!p.position }) : []
  var request = list.filter(function (p) { return p.kind === 'request' })[0] || null
  var pinOf = {}
  var seen = {}

  list.forEach(function (p) {
    if (p.kind === 'pin') {
      pinOf[String(p.optionId !== undefined && p.optionId !== null ? p.optionId : 'no:' + p.optionNo)] = p
    }
  })

  return list.map(function (p) {
    var from = null
    var name

    if (p.kind === 'request') {
      name = 'Requested point'
    } else if (p.kind === 'pin') {
      name = 'Option ' + p.optionNo
      from = request
    } else {
      var owner = String(p.optionId !== undefined && p.optionId !== null ? p.optionId : 'no:' + p.optionNo)
      var countKey = owner + ':' + p.type
      var k = (seen[countKey] = (seen[countKey] || 0) + 1)

      name = extraMarkerName(p.type, k) + (o.withOption ? ' · option ' + p.optionNo : '')
      from = pinOf[owner] || null
    }

    return {
      key: p.key,
      kind: p.kind,
      type: p.type,
      optionId: p.optionId === undefined ? null : p.optionId,
      optionNo: p.optionNo === undefined ? null : p.optionNo,
      markerIndex: p.markerIndex === undefined ? null : p.markerIndex,
      name: name,
      icon: p.icon,
      lat: round7(p.position.lat),
      lng: round7(p.position.lng),
      metres: from ? Math.round(metresBetween(from.position.lat, from.position.lng, p.position.lat, p.position.lng)) : null,
      from: from ? (from.kind === 'request' ? 'the requested point' : 'option ' + from.optionNo) : null,
      deletable: p.kind === 'extra' && !!o.canDelete
    }
  })
}

/**
 * The list under the buttons, one row per extra marker in the order stored, numbered within its type (decision 149): [{ index, type, k, name,
 * icon, metres, fresh, text, aria }]. `index` is the marker's place in `option.markers`; `metres` its distance from the option's own pin (the SPA's own,
 * for the row only: the option's three distance fields are the server's); `fresh` says it has not been dragged yet (`freshKeys` holds the keys from
 * markerKey, for this session only), and then `text` is the instruction instead of the distance. A legacy `option` marker, or one with no position,
 * has no row.
 */
export function extraMarkerRows (option, freshKeys) {
  var rows = []
  var seen = {}
  var pin = option ? pointOf(option.actualLat, option.actualLng) : null

  ;(option && Array.isArray(option.markers) ? option.markers : []).forEach(function (m, index) {
    if (!m || !has(EXTRA_LABELS, String(m.type))) {
      return
    }

    var k = (seen[m.type] = (seen[m.type] || 0) + 1)
    var at = m.position ? pointOf(m.position.lat, m.position.lng) : null

    if (!at) {
      return
    }

    var name = extraMarkerName(m.type, k)
    var fresh = (freshKeys || []).indexOf(markerKey(m)) !== -1
    var metres = pin ? Math.round(metresBetween(pin.lat, pin.lng, at.lat, at.lng)) : null
    var text = fresh ? DRAG_TELLS[m.type] : (metres === null ? '' : metres + ' m from option ' + option.optionNo)

    rows.push({
      index: index,
      type: m.type,
      k: k,
      name: name,
      icon: extraMarkerLook(m.type, option.optionNo, k).icon,
      metres: metres,
      fresh: fresh,
      text: text,
      aria: name + ' marker' + (text ? ', ' + (fresh ? 'just added: ' + text.charAt(0).toLowerCase() + text.slice(1) : text) : '')
    })
  })

  // Grouped access, power, building, and within a type in the order added (UX spec 2.4.2 item 2): a stable sort on the type's place.
  return rows.map(function (r, i) { return { r: r, i: i } }).sort(function (a, b) {
    return (EXTRA_MARKER_TYPES.indexOf(a.r.type) - EXTRA_MARKER_TYPES.indexOf(b.r.type)) || (a.i - b.i)
  }).map(function (x) { return x.r })
}

/** A marker's identity for "just added": its type and where it was dropped. Dragging it changes the key, so the instruction goes. */
export function markerKey (m) {
  return m && m.position ? String(m.type) + ':' + Number(m.position.lat) + ':' + Number(m.position.lng) : ''
}

/**
 * Where a new marker is dropped (UX spec 2.4.2, U-17): 48 px beside the option's own pin at the map's current zoom, access to the EAST, power to the
 * WEST, building to the SOUTH, so three added in a row never stack. Web Mercator: a pixel is 156543.03 * cos(lat) / 2^zoom metres. Null without a
 * pin or a type.
 */
export function markerDropPoint (type, pin, zoom) {
  var at = pin ? pointOf(pin.lat, pin.lng) : null
  var z = Number(zoom)

  if (!at || !has(EXTRA_LABELS, String(type))) {
    return null
  }

  var metres = 48 * 156543.03392 * Math.cos(at.lat * Math.PI / 180) / Math.pow(2, isNaN(z) || z < 0 ? 17 : z)
  var dLng = metres / (111320 * Math.cos(at.lat * Math.PI / 180))
  var dLat = metres / 110574
  var lat = at.lat
  var lng = at.lng

  if (type === 'access') {
    lng += dLng
  } else if (type === 'power') {
    lng -= dLng
  } else {
    lat -= dLat
  }

  return { lat: Math.round(lat * 1e7) / 1e7, lng: Math.round(lng * 1e7) / 1e7 }
}

/**
 * The entry a new marker adds to the option's `markers` list (UX spec 2.4.2 item 6): { type, position: { lat, lng }, icon, infoText }. `icon` and `infoText`
 * are written from the type and the option's number for other readers of the record; RTA screens work them out again. Null without a pin.
 */
export function newExtraMarker (type, option, zoom) {
  var pin = option ? pointOf(option.actualLat, option.actualLng) : null
  var at = markerDropPoint(type, pin, zoom)

  if (!at) {
    return null
  }

  return { type: type, position: at, icon: extraMarkerLook(type, option.optionNo).icon, infoText: EXTRA_LABELS[type] }
}

/* ------------------------------------------------------------------------
   The request: the point the Telco wants options found around (decisions 153, 156)
------------------------------------------------------------------------ */

// The request as an RTA carries it: `state.nominal` of a full action (rtaStage.rtaOf puts the compact `rta`'s `nominal` there too).
function nominalOf (rta) {
  var n = rta && rta.state && rta.state.nominal

  return n && typeof n === 'object' ? n : null
}

/**
 * The request's point as { lat, lng }, or null when it has none (P-S22: a null point, one coordinate without the other and a legacy (0, 0)
 * are all "no point"). The crosshairs, the map centre and the coordinates table's base all read it.
 */
export function requestPoint (rta) {
  var n = nominalOf(rta)

  return n ? pointOf(n.latitude, n.longitude) : null
}

/**
 * The request in the shape of a site (siteName, siteId, latitude, longitude, region, state, district), which is what the option screens
 * (AcquireDetails, OptionAction, the Options table, the step Map) were written to read. An RTA has no site row until it completes
 * (decision 5); `state.nominal` stands in. `id` is null on purpose: there is no site to attach files or links to. The name is the
 * request's Site Name when it has one (158: the generated name is sent as `nominal.siteName`), else the RTA's title; the Site ID is the
 * request's, else the reference. Null when the RTA has no request.
 */
export function requestSite (rta) {
  var n = nominalOf(rta)

  if (!rta || typeof rta !== 'object' || !n) {
    return null
  }

  var at = requestPoint(rta)

  return {
    id: null,
    siteName: present(n.siteName) ? String(n.siteName) : rtaTitle(rta),
    siteId: present(n.siteId) ? String(n.siteId) : referenceOf(rta),
    latitude: at ? at.lat : null,
    longitude: at ? at.lng : null,
    region: n.region,
    state: n.state,
    district: n.district,
    subdistrict: n.subdistrict,
    zone: n.zone,
    count: rta.liveOptionCount !== undefined ? Number(rta.liveOptionCount) || 0 : 0
  }
}

// The fields a new option starts with from the request (decision 158 Q2, delta 1.4): the same names on both sides, except the point.
var REQUEST_TO_OPTION = ['region', 'state', 'district', 'subdistrict', 'zone', 'stationType', 'structure_id', 'structureHeight', 'antennaHeight', 'width', 'depth']

/**
 * What a NEW option is pre-filled with from the request (decision 158 Q2; the user edits it before Save): the point (the pin starts on the
 * crosshairs: `actualLat`, `actualLng`), the locality, Station Type, Structure Type, Structure Height, Antenna AGL, Width and Depth.
 * Only keys the request holds are returned, so an empty request pre-fills nothing. `distanceFromNominal` is never in it: the server owns it (P-S6).
 */
export function optionDefaultsFromRequest (rta) {
  var n = nominalOf(rta)
  var out = {}

  if (!n) {
    return out
  }

  var at = requestPoint(rta)

  if (at) {
    out.actualLat = at.lat
    out.actualLng = at.lng
  }

  REQUEST_TO_OPTION.forEach(function (k) {
    if (present(n[k])) {
      out[k] = n[k]
    }
  })

  return out
}

// An option's pin by its number (decision 146 reversed by 155): the SPA derives the label and the colour on RTA screens from the CURRENT
// number. Option 1 is an ordinary option: it takes the first of the legacy option-pin order (OptionAction.getIcon at d14e0b84), which is
// also what the pins stored on legacy and completed sites show. The blue crosshairs are the REQUEST's point (156), never an option's. The
// access, power and building markers keep their own pins and are not in this table. (The API no longer rewrites stored option markers,
// plan-v2 P-S2.)
var PIN_LOOKS = {
  1: ['static/map/green-map.png', 'green'],
  2: ['static/map/blue-map.png', 'blue'],
  3: ['static/map/yellow-map.png', 'yellow'],
  4: ['static/map/red-map.png', 'red'],
  5: ['static/map/black-map.png', 'black'],
  6: ['static/map/grey-map.png', 'grey'],
  7: ['static/map/google-map.png', 'google']
}

/**
 * The look of an option's pin from its number: { label: 'Option 2', icon: 'static/map/blue-map.png', color: 'blue' }. Option 8 and
 * above (and a number that is none) get the outline pin. The number is the CURRENT one: it moves when an option is deleted.
 */
export function optionMarkerLook (optionNo) {
  var n = Number(optionNo)
  var known = !isNaN(n) && has(PIN_LOOKS, String(n))

  return {
    label: !isNaN(n) && n > 0 ? 'Option ' + n : 'Option',
    icon: known ? PIN_LOOKS[String(n)][0] : 'static/map/outline-map.png',
    color: known ? PIN_LOOKS[String(n)][1] : 'outline'
  }
}

// The request's pin: the blue crosshairs, with no number (the request is not an option, U-12). Hover reads "Requested point".
export var REQUEST_PIN = { icon: 'static/map/blue-cross-hairs-map.png', color: 'crosshairs', label: 'Requested point' }

/** The look of the request's crosshairs: { label: 'Requested point', icon, color: 'crosshairs' }. (A copy; it has no option number.) */
export function requestMarkerLook () {
  return { label: REQUEST_PIN.label, icon: REQUEST_PIN.icon, color: REQUEST_PIN.color }
}

// The access marker's road pin follows its option's pin (decisions 147, 151, 155): options 1 to 6 have their own colour road pin, option 7
// (google) takes the plain `road-map.png`, and options 8 and up share `outline-road-map.png`, as the outline pin is shared. The request has
// no access marker, so there is no crosshairs road pin (`crosshairs-road-map.png` is no longer used). The specs check the paths.
var ACCESS_PINS = {
  green: 'static/map/green-road-map.png',
  blue: 'static/map/blue-road-map.png',
  yellow: 'static/map/yellow-road-map.png',
  red: 'static/map/red-road-map.png',
  black: 'static/map/black-road-map.png',
  grey: 'static/map/grey-road-map.png',
  google: 'static/map/road-map.png',
  outline: 'static/map/outline-road-map.png'
}

// Power and building markers keep ONE image each, whatever their option.
var SINGLE_PINS = { power: 'static/map/power-map.png', building: 'static/map/building-map.png' }

var EXTRA_LABELS = { access: 'Access', power: 'Power', building: 'Building' }

/** The marker types an option can still add by hand (decision 144: the option type is gone, each option IS a pin). */
export var EXTRA_MARKER_TYPES = ['access', 'power', 'building']

/** The road pin of an access marker of option `optionNo`: the option's own colour (decisions 146, 147, 155). */
export function accessPinFor (optionNo) {
  return ACCESS_PINS[optionMarkerLook(optionNo).color]
}

/**
 * An access, power or building marker: { icon, label }. The icon is the option's colour road pin for access, the single image for power and
 * building; the label is the hover tooltip (U-12, UX spec 4.5): "Access 1 · Option 2", numbered WITHIN its type (`k`, decision 149), with
 * " · Option {n}" when the number is known and no number when `k` is not given ("Access"). Null for any other type (a legacy `option` marker
 * included: the option's own pin is drawn from its point).
 */
export function extraMarkerLook (type, optionNo, k) {
  var key = String(type)

  if (!has(EXTRA_LABELS, key)) {
    return null
  }

  var n = Number(optionNo)

  return {
    icon: key === 'access' ? accessPinFor(optionNo) : SINGLE_PINS[key],
    label: EXTRA_LABELS[key] + (Number(k) > 0 ? ' ' + Number(k) : '') + (!isNaN(n) && n > 0 ? ' · Option ' + n : '')
  }
}

/**
 * The access marker of option `optionNo` (the name the UX spec uses, §2.4 map): { icon, label } = extraMarkerLook('access', optionNo). Like the
 * option's own pin it follows the option's CURRENT number, so a renumber recolours it. accessPinFor is the icon alone.
 */
export function accessMarkerLook (optionNo) {
  return extraMarkerLook('access', optionNo)
}

function optionKey (option) {
  return 'option-' + (present(option.id) ? option.id : 'no' + option.optionNo)
}

function pointOf (lat, lng) {
  return hasPoint(lat, lng) ? { lat: coordinate(lat), lng: coordinate(lng) } : null
}

// The extra markers of an option as map markers. `index` is the marker's place in the option's `markers` list, so a key is stable while
// the list is not reordered (the map tracks a drag by it). A marker with no real position is skipped.
function extraMarkers (option, canEdit, opens) {
  var out = []
  var seen = {}

  ;(Array.isArray(option.markers) ? option.markers : []).forEach(function (m, index) {
    // The marker's number within its type, in the order stored: "Access 1", "Access 2" (decision 149). A marker with no position still counts.
    var k = m && has(EXTRA_LABELS, String(m.type)) ? (seen[m.type] = (seen[m.type] || 0) + 1) : 0
    var look = m ? extraMarkerLook(m.type, option.optionNo, k) : null
    var position = look && m.position ? pointOf(m.position.lat, m.position.lng) : null

    if (!look || !position) {
      return
    }

    out.push({
      key: optionKey(option) + ':' + m.type + ':' + index,
      kind: 'extra',
      type: m.type,
      optionId: option.id,
      optionNo: option.optionNo,
      markerIndex: index,
      position: position,
      icon: look.icon,
      label: look.label,
      title: m.infoText || '',
      draggable: canEdit,
      readOnly: !canEdit,
      opensOption: opens
    })
  })

  return out
}

function pinMarker (option, draggable, opens) {
  var position = pointOf(option.actualLat, option.actualLng)

  if (!position) {
    return null
  }

  var look = optionMarkerLook(option.optionNo)

  return {
    key: optionKey(option) + ':pin',
    kind: 'pin',
    type: 'option',
    optionId: option.id,
    optionNo: option.optionNo,
    optionStatus: option.optionStatus,
    position: position,
    icon: look.icon,
    color: look.color,
    label: look.label,
    draggable: draggable,
    readOnly: !draggable,
    opensOption: opens
  }
}

// The request's crosshairs as a map marker (decision 156), or null when the request has no point. `draggable` only on the request map and
// only for a viewer who may move it; everywhere else it is a read-only reference.
function requestMarker (rta, draggable) {
  var at = requestPoint(rta)

  if (!at) {
    return null
  }

  var look = requestMarkerLook()

  return {
    key: 'request:pin',
    kind: 'request',
    type: 'request',
    optionId: null,
    optionNo: null,
    numbered: false,
    position: at,
    icon: look.icon,
    color: look.color,
    label: look.label,
    draggable: draggable === true,
    readOnly: draggable !== true,
    opensOption: null
  }
}

/**
 * The pins of ONE option's map (decisions 147 reworked by 155, 156; this narrows 144's "sees the other options' pins"), for EVERY option,
 * Option 1 included:
 *   the request crosshairs as a READ-ONLY reference (skipped when the request has no point), the option's own coloured pin, and its own extra
 *   markers. No other option's pin.
 * The own pin and the extras are draggable only when the viewer may edit the option's details (canEditOptionDetails: decision 126 and nothing
 * else; the Option 1 lock of 144 moved to the request, 157). `viewer` is { org, profile }; without it nothing is draggable. An option with no
 * point has no pin (the caller places it); a new option starts on the request's point.
 *
 * Each marker: { key, kind: 'request' | 'pin' | 'extra', type, optionId, optionNo, position: { lat, lng }, icon, label, draggable,
 * readOnly, ... }. `key` is stable (option id and marker place) so the map can track a drag. `allOptions` is accepted for the caller's
 * symmetry and is not read.
 */
export function markersForOptionMap (option, rta, allOptions, viewer) {
  if (!option) {
    return []
  }

  viewer = viewer || null

  var out = []
  var canEdit = !!viewer && canEditOptionDetails(option, rta, viewer.org, viewer.profile)
  var reference = requestMarker(rta, false)

  if (reference) {
    out.push(reference)
  }

  var pin = pinMarker(option, canEdit, option.id)

  if (pin) {
    out.push(pin)
  }

  return out.concat(extraMarkers(option, canEdit, option.id))
}

/**
 * The pins of the step Map (Searching and Client Review; decisions 144, 147, 156): the request crosshairs, then every option's pin and every
 * option's extra markers, ALL read-only. A click on an option opens it (`opensOption`), and the hover tooltip is the marker's `label`
 * ("Requested point", "Option 2", "Access · Option 2"). Options in number order, each pin before its extras; an option with no point has no pin;
 * no request point, no crosshairs.
 */
export function markersForStepMap (rta, allOptions) {
  var list = (Array.isArray(allOptions) ? allOptions : []).filter(function (o) { return !!o })

  list.sort(function (a, b) { return Number(a.optionNo) - Number(b.optionNo) })

  var out = []
  var reference = requestMarker(rta, false)

  if (reference) {
    out.push(reference)
  }

  list.forEach(function (option) {
    var pin = pinMarker(option, false, option.id)

    if (pin) {
      out.push(pin)
    }

    out = out.concat(extraMarkers(option, false, option.id))
  })

  return out
}

/**
 * The pins of the Step 1 map (delta 2.5): the request crosshairs, draggable while the viewer may edit the request's point, and every option's
 * pin, read-only (a click opens the option). No extra markers are drawn. `viewer` is { org, profile }; without it the crosshairs are read-only.
 * The point is two fields, so the viewer needs both (rtaStage.canEditRequestField for latitude and longitude, which always agree).
 */
export function markersForRequestMap (rta, allOptions, viewer) {
  var list = (Array.isArray(allOptions) ? allOptions : []).filter(function (o) { return !!o })

  list.sort(function (a, b) { return Number(a.optionNo) - Number(b.optionNo) })

  var out = []
  var canMove = !!viewer && canEditRequestField('latitude', rta, viewer.org, viewer.profile) &&
    canEditRequestField('longitude', rta, viewer.org, viewer.profile)
  var crosshairs = requestMarker(rta, canMove)

  if (crosshairs) {
    out.push(crosshairs)
  }

  list.forEach(function (option) {
    var pin = pinMarker(option, false, option.id)

    if (pin) {
      out.push(pin)
    }
  })

  return out
}

/* ------------------------------------------------------------------------
   The map helpers (lib/rtaNominal re-exports them for the request screens)
------------------------------------------------------------------------ */

function blank (v) {
  return v === undefined || v === null || String(v).trim() === ''
}

/** A coordinate: a number, or null when nothing (or 0, the unset value: no point in Malaysia sits on 0) was entered. */
export function coordinate (v) {
  var n = Number(v)

  return blank(v) || isNaN(n) || n === 0 ? null : n
}

/** Is there a point (P-S22)? Both coordinates are real numbers: null, '', NaN and 0 (a legacy (0, 0)) are none. */
export function hasPoint (lat, lng) {
  return coordinate(lat) !== null && coordinate(lng) !== null
}

// The centre of Malaysia, zoomed out. Carlos (decision 124): "default to Malaysia's centre (about 4.2, 101.97), zoomed out".
export var MALAYSIA_CENTRE = { lat: 4.2, lng: 101.97, zoom: 6 }

// Nothing in the app turns a locality into coordinates (the locality tree has names only), so the starting view of an RTA with no point
// is a small table of APPROXIMATE centres of the states and regions of the tree. It places the view, never a pin: nothing is saved
// from it. Keys are the names in src/components/config/locality/malaysia.json.
var STATE_CENTRES = {
  'Federal Territory': { lat: 3.14, lng: 101.69 },
  'Johor': { lat: 2.0, lng: 103.3 },
  'Kedah': { lat: 6.0, lng: 100.55 },
  'Kelantan': { lat: 5.4, lng: 102.0 },
  'Melaka': { lat: 2.2, lng: 102.25 },
  'Negeri Sembilan': { lat: 2.75, lng: 102.25 },
  'Pahang': { lat: 3.8, lng: 102.5 },
  'Penang': { lat: 5.4, lng: 100.35 },
  'Perak': { lat: 4.6, lng: 101.1 },
  'Perlis': { lat: 6.45, lng: 100.2 },
  'Selangor': { lat: 3.35, lng: 101.5 },
  'Terengganu': { lat: 4.9, lng: 103.0 }
}

var REGION_CENTRES = {
  'Semenanjung': { lat: 4.2, lng: 102.0 },
  'Sabah': { lat: 5.4, lng: 117.0 },
  'Sarawak': { lat: 2.5, lng: 113.5 }
}

function known (table, name) {
  return !blank(name) && has(table, name) ? table[name] : null
}

/**
 * { lat, lng, zoom, source } for the locality path `loc` ([region, state, district, ...]): the state's centre when it is one the table
 * knows (zoom 9), else the region's (zoom 7), else the centre of Malaysia (zoom 6). A Sabah or Sarawak division resolves to its region.
 */
export function mapCentre (loc) {
  loc = loc || []

  var state = known(STATE_CENTRES, loc[1])

  if (state) {
    return { lat: state.lat, lng: state.lng, zoom: 9, source: 'state' }
  }

  var region = known(REGION_CENTRES, loc[0])

  if (region) {
    return { lat: region.lat, lng: region.lng, zoom: 7, source: 'region' }
  }

  return { lat: MALAYSIA_CENTRE.lat, lng: MALAYSIA_CENTRE.lng, zoom: MALAYSIA_CENTRE.zoom, source: 'malaysia' }
}

/**
 * The centre of the markers of a map with no request point: the middle of their bounding box, as { lat, lng }, or null when none has a
 * position. (The map fits all of them itself; this only gives the map a centre to start from.)
 */
export function markersCentre (markers) {
  var lats = []
  var lngs = []

  ;(markers || []).forEach(function (m) {
    var p = m && m.position
    var lat = p ? Number(p.lat) : NaN
    var lng = p ? Number(p.lng) : NaN

    if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
      lats.push(lat)
      lngs.push(lng)
    }
  })

  if (lats.length === 0) {
    return null
  }

  return {
    lat: (Math.min.apply(null, lats) + Math.max.apply(null, lats)) / 2,
    lng: (Math.min.apply(null, lngs) + Math.max.apply(null, lngs)) / 2
  }
}

/* ------------------------------------------------------------------------
   Bodies
------------------------------------------------------------------------ */

// API DEV-10 as API-6 changes it: the three allow-lists of an option. A key outside the caller's list is REFUSED by the server on an RTA option
// (P-S28: 422 read_only_fields; it used to be dropped), so it is not sent. `handoverStatus` (Site Status on handover, I-15) is Tower Co-only. The FOUR distance fields are NOT here: the server
// computes `distanceFromNominal` from the request's point (P-S6, API-30) and the other three as the closest marker of each type
// (decision 149, API-25, D152): `distanceAccessRoad`, `distanceNearestPower`, `distanceNearestBuilding`. A sent value is refused (422
// read_only_fields on an RTA option); the SPA only displays them. An option may hold SEVERAL extra markers of each type.
export var TC_ONLY_FIELDS = [
  'ref', 'report', 'supplier_id', 'owner_id', 'owner', 'phone', 'email', 'fromTime', 'hoursAccessSite',
  'weekDaysOnly', 'landType', 'classificationType', 'surveyDate',
  'commercialPowerAvailable', 'guidelines',
  'handoverStatus'
]

// The old name of the same list (XOL-67 B4): kept so a caller of it is not broken.
export var TOWER_CO_FIELDS = TC_ONLY_FIELDS

// Shared by both sides: `width` and `depth` (Structure Details, I-15) join the list.
export var SHARED_FIELDS = [
  'title', 'stationType', 'antennaHeight', 'structure_id', 'structureHeight', 'width', 'depth', 'actualLat', 'actualLng',
  'address', 'postCode', 'zone', 'subdistrict', 'district', 'state', 'region', 'country', 'markers'
]

export var CLIENT_FIELDS = ['clientRef']

// Never in an option PUT (API DEV-11): the live review, the submitted layer and everything the server owns.
// A changed `clientStatus` / `clientRemarks` is answered with 410 use_respond.
export var SERVER_OWNED_FIELDS = [
  'clientStatus', 'clientRemarks', 'submittedStatus', 'submittedRemarks', 'submitted_at', 'submitted_by',
  'optionStatus', 'responseChanged', 'client_status_at', 'client_status_by',
  'submitted_by_party', 'client_status_by_party',
  'site_id', 'client_id', 'action_id', 'optionNo', 'idempotency_key', 'added_by_party', 'declineReason',
  'declined_at', 'declined_by', 'proposed_at', 'proposed_by',
  'final_at', 'final_by', 'siteStatus', 'distanceFromNominal', 'distanceAccessRoad', 'distanceNearestPower', 'distanceNearestBuilding', 'created_at', 'created_by',
  'updated_at', 'updated_by'
]

/**
 * XOL-67 B11 (decision 129, API DEV-64 review): the keys a caller may WRITE to an option, from what its gates allow: `tc` the Tower Co-only
 * fields (TC_ONLY_FIELDS: Ref, Report, the land and owner fields, the survey and distance fields, Site Status on handover), `shared` the
 * either-party fields, `clientRef` the client's reference. The API refuses a field outside the caller's list (P-S28: 422 read_only_fields on an RTA
 * option; a legacy non-RTA option still drops it and answers 200), so the SPA never sends one: this is the single place the lists meet the gates (AcquireDetails.sendable).
 */
export function writableFields (gates) {
  gates = gates || {}

  return (gates.tc ? TC_ONLY_FIELDS : []).concat(gates.shared ? SHARED_FIELDS : [], gates.clientRef ? CLIENT_FIELDS : [])
}

/**
 * The body of POST /candidate (an add) or PUT /candidate/{id} (a content save): only the keys the caller may
 * write (API DEV-10), and only those the option has.
 *
 * @param {Object} option
 * @param {String} party  'tc' (a Tower Co editor: its own fields and the shared ones) or 'client'
 * @param {Object} opts   { shared: true when the client may write the shared fields, clientRef: false to leave its reference out }
 */
export function contentBody (option, party, opts) {
  opts = opts || {}

  var keys = []
  var body = {}
  var src = option || {}

  if (party === 'tc') {
    keys = TC_ONLY_FIELDS.concat(SHARED_FIELDS)
  } else {
    keys = (opts.shared ? SHARED_FIELDS : []).concat(opts.clientRef === false ? [] : CLIENT_FIELDS)
  }

  keys.forEach(function (k) {
    if (has(src, k) && src[k] !== undefined) {
      body[k] = src[k]
    }
  })

  // The supplier select holds the organisation (an object); the column is supplier_id.
  if (party === 'tc' && !has(body, 'supplier_id') && src.supplier && present(src.supplier.id)) {
    body.supplier_id = src.supplier.id
  }

  return body
}

// One value the way it is compared: nothing (null, undefined, '') is one thing; a number and its numeric text are one; a list or an object by its JSON.
function comparable (v) {
  if (v === undefined || v === null || v === '') {
    return null
  }

  if (typeof v === 'object') {
    return JSON.stringify(v)
  }

  if (typeof v === 'string' && v.trim() !== '' && !isNaN(Number(v))) {
    return Number(v)
  }

  if (typeof v === 'number' && isNaN(v)) {
    return null
  }

  return v
}

/** Do two values of an option field mean the same? (nothing = null = '', 3.1 = '3.1', objects by their JSON.) */
export function sameValue (a, b) {
  return comparable(a) === comparable(b)
}

/**
 * The option's auto-save (decision 125): the keys of `current` (a contentBody) whose value is not the one the server last took
 * (`saved`, a contentBody taken at load and after each answer). A key that is gone from `current` is not a change. The result is the
 * body of the partial PUT /candidate/{id}; `{}` means there is nothing to send. A cleared field goes as null.
 */
export function contentChanges (saved, current) {
  saved = saved && typeof saved === 'object' ? saved : {}
  current = current && typeof current === 'object' ? current : {}

  var out = {}

  Object.keys(current).forEach(function (k) {
    if (!sameValue(saved[k], current[k])) {
      out[k] = present(current[k]) && !(typeof current[k] === 'number' && isNaN(current[k])) ? current[k] : null
    }
  })

  return out
}

/**
 * The line over the Details, Report and Land Owner panes once the option is locked (decision 126; UX spec 4.5), or '' when it is not:
 *   a submitted Approved or Declined   "Locked: this option's review is submitted. Change its review to Pending to edit the fields."
 *   Finalize                           "Locked while {Tower Co} finalizes the site."
 *   a closed RTA, or a terminal option "This RTA is closed. The option can't be changed."
 */
export function detailsLockNote (option, rta, ctx) {
  ctx = ctx || {}

  if (!option || !rta) {
    return ''
  }

  var lock = optionDetailsLock(option, rta)

  if (!lock) {
    return ''
  }

  if (lock.reason === 'submitted') {
    return 'Locked: this option\'s review is submitted. Change its review to Pending to edit the fields.'
  }

  if (lock.reason === 'finalize') {
    return 'Locked while ' + (ctx.towerCo || 'the Tower Co') + ' finalizes the site.'
  }

  return isTerminal(rta) || lock.reason === 'terminal' ? 'This RTA is closed. The option can\'t be changed.' : ''
}

/* ------------------------------------------------------------------------
   The coordinate table of the option layer
------------------------------------------------------------------------ */

export var NOT_SET = 'Not set'

function point (v) {
  var n = Number(v)

  return present(v) && !isNaN(n) && n !== 0 ? n : null
}

// Haversine in metres, R = 6371 km, to 2 places: the API's rule for `distanceFromNominal` (DEV-62; v2.1: measured from the request point again).
// Seven decimals (about 1 cm), as the option's own Lat / Lng are stored.
function round7 (n) {
  return Math.round(Number(n) * 1e7) / 1e7
}

function metresBetween (lat1, lng1, lat2, lng2) {
  var rad = Math.PI / 180
  var dLat = (lat2 - lat1) * rad
  var dLng = (lng2 - lng1) * rad
  var a = Math.pow(Math.sin(dLat / 2), 2) + Math.pow(Math.sin(dLng / 2), 2) * Math.cos(lat1 * rad) * Math.cos(lat2 * rad)

  return Math.round(6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)) * 1000 * 100) / 100
}

/**
 * The cells of the Request / Candidate / Distance table, as text: { nominalLat, nominalLng, candLat, candLng, distance }. (The
 * `nominal*` keys are the REQUEST's point again, on every option, Option 1 included: delta 2.5.) A missing point (nothing,
 * NaN or 0, which is how the API's null arrives once it is cast to a number) reads "Not set", never "NaN" or "0". The distance
 * appears only when BOTH points exist: the stored `distance` if it is a number, else the one the points give.
 */
export function coordinateRow (p) {
  p = p || {}

  var nLat = point(p.nominalLat)
  var nLng = point(p.nominalLng)
  var cLat = point(p.candLat)
  var cLng = point(p.candLng)
  var both = nLat !== null && nLng !== null && cLat !== null && cLng !== null
  var stored = present(p.distance) && !isNaN(Number(p.distance)) ? Number(p.distance) : null

  return {
    nominalLat: nLat === null ? NOT_SET : String(nLat),
    nominalLng: nLng === null ? NOT_SET : String(nLng),
    candLat: cLat === null ? NOT_SET : String(cLat),
    candLng: cLng === null ? NOT_SET : String(cLng),
    distance: both ? String(stored !== null ? stored : metresBetween(nLat, nLng, cLat, cLng)) : NOT_SET
  }
}

/** The body of PUT /candidate/{id}/response (API-7): { clientStatus: 'Pending' | 'Approved' | 'Declined', clientRemarks }. */
export function responseBody (live) {
  return {
    clientStatus: live && present(live.status) ? live.status : 'Pending',
    clientRemarks: live && present(live.remarks) ? live.remarks : null
  }
}

/* ------------------------------------------------------------------------
   Toast sentences and confirms
------------------------------------------------------------------------ */

var SUBMIT_VERB = { Approved: 'approved', Declined: 'declined', Pending: 'set back to Pending' }

/**
 * The sentence a command's toast says (UX spec 4.6). `p` holds what the sentence names: { n (option number), status (the submitted
 * status), side ('tc' when a Tower Co user records the review for the client), backToDraft (an add in Client Review, or every option left
 * declined), reversal (the Approved option was taken out of Step 4), stage ('Client Review' | 'Searching': where a reversal put the RTA),
 * target ('client' | 'towerco' | 'self': who a Propose went to), name, reference }.
 *
 *   create        RTA created: {name} ({reference}). · ... Nobody was e-mailed: Send E-mails is off. (`p.mailOff`; not for a Tower Co client,
 *                 `p.target` 'self'). The delivery line that follows is the response's counts-only notice (requestActivity.noticeOutcome:
 *                 "2 recipients at TowerCo Alpha are being e-mailed.")
 *   add           Option 4 added.  (in Client Review: + The RTA is back in Searching: propose the options again when they're ready.)
 *   delete        Option 3 deleted.
 *   propose       Options proposed to {Telco | Tower Co}. [Nobody was e-mailed: Send E-mails is off. (`p.mailOff`)] · Options moved to Client Review. (a Tower Co client)
 *                 (the e-mail line that follows is the response's notice, requestActivity.noticeOutcome)
 *   backToDraft   Back in Searching. Change the options, then propose them again.
 *   submit        Review submitted: option 2 approved.  ·  Review recorded for {Telco}: option 2 approved.
 *                 (every option declined: + Every option is declined, so the RTA is back in Searching.)
 *                 (a reversal: Review submitted: option 2 is no longer approved. The RTA is back in Client Review. -- a warning toast)
 *   saved         [Title #n] updated
 */
export function sentence (kind, ctx, p) {
  ctx = ctx || {}
  p = p || {}

  var telco = ctx.telco || 'the client'
  var tc = ctx.towerCo || 'the Tower Co'
  var n = p.n || ''

  switch (kind) {
    case 'create':
      // UX spec 4.6 (decision 158 Q1): the sentence alone; what the e-mail did follows it from the response's counts-only notice
      // (requestActivity.noticeOutcome: "2 recipients at TowerCo Alpha are being e-mailed."). Send E-mails off says so here, because that notice
      // has nothing to say. A Tower Co client (nobody to e-mail) and an answer with no notice read the sentence alone.
      return 'RTA created: ' + (p.name || 'this RTA') + (p.reference ? ' (' + p.reference + ')' : '') + '.' +
        (p.mailOff === true && p.target !== 'self' ? ' Nobody was e-mailed: Send E-mails is off.' : '')
    case 'add':
      return 'Option ' + n + ' added.' + (p.backToDraft ? ' The RTA is back in Searching: propose the options again when they\'re ready.' : '')
    case 'delete':
      return 'Option ' + n + ' deleted.'
    case 'propose':
      if (p.target === 'self') {
        return 'Options moved to Client Review.'
      }

      return 'Options proposed to ' + (p.target === 'towerco' ? tc : telco) + '.' + (p.mailOff === true ? ' Nobody was e-mailed: Send E-mails is off.' : '')
    case 'backToDraft':
      return 'Back in Searching. Change the options, then propose them again.'
    case 'submit':
      if (p.reversal) {
        return 'Review submitted: option ' + n + ' is no longer approved. The RTA is back in ' + (p.stage === 'Searching' ? 'Searching' : 'Client Review') + '.'
      }

      return (p.side === 'tc' ? 'Review recorded for ' + telco + ': option ' : 'Review submitted: option ') + n + ' ' + (SUBMIT_VERB[p.status] || SUBMIT_VERB.Pending) + '.' +
        (p.backToDraft ? ' Every option is declined, so the RTA is back in Searching.' : '')
    case 'saved':
      return '[' + (p.title || 'Option') + ' #' + n + '] updated'
  }

  return ''
}

/**
 * Why the client's Delete of its own option is disabled (decision 122, mapped by I-25), by the reason of
 * rtaStage.deleteBlockedReason. The button stays on screen, disabled, so the client sees the option is not gone for a reason.
 *   review  the option is in Review: Back to Searching first
 * '' for no reason (the button is enabled, or not drawn).
 */
export function deleteBlockedCopy (reason, ctx) {
  ctx = ctx || {}

  if (reason === 'review') {
    return 'Can\'t delete an option in review. Press Back to Searching first.'
  }

  return ''
}

/**
 * A confirm: { title, message, confirmButtonText, cancelButtonText, type }.
 *
 *   backToDraft   Back to Searching (U-7: it discards the reviews that weren't submitted, I-8; the route and the key keep the v2 name)
 *   submitUndo    a Submit review that takes the Approved option out of Step 4 (U-8)
 *   delete        a row delete
 *   addInReview   NOT a dialog: the el-alert at the top of the New Option drawer in Review (U-7). { title, message, type } only.
 *
 * `p` { n (option number), clears (a saved decision is cleared), toStage ('Client Review' | 'Searching', when the caller knows where the RTA goes) }.
 * (The Propose confirm is an RtaDialog descriptor, like the Decline and Reject RTA… dialogs: rtaDecision.proposeConfirm.)
 */
export function confirmCopy (kind, ctx, p) {
  ctx = ctx || {}
  p = p || {}

  var tc = ctx.towerCo || 'the Tower Co'
  var n = p.n || ''

  switch (kind) {
    case 'backToDraft':
      return {
        title: 'Back to Searching?',
        message: 'The RTA goes back to Searching and the options go back to Draft so they can be changed. Submitted reviews are kept; reviews that weren\'t submitted are discarded. Nobody is e-mailed.',
        confirmButtonText: 'Back to Searching',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }

    case 'addInReview':
      return {
        title: 'Adding an option sends this RTA back to Searching',
        message: 'The options in review go back to Draft, reviews that weren\'t submitted are discarded, and the options must be proposed again.',
        type: 'warning'
      }

    case 'submitUndo':
      return {
        title: 'Change the approved option\'s review?',
        message: (n ? 'Option ' + n : 'The approved option') + ' will no longer be approved, so the RTA leaves Step 4 and goes back to ' +
          (p.toStage ? p.toStage : 'Client Review (Searching, when no option is left in review)') + '.' +
          (p.clears ? ' ' + tc + '\'s saved decision is cleared; its remarks are kept.' : ''),
        confirmButtonText: 'Submit review',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }

    case 'delete':
      return {
        title: 'Delete option',
        message: 'Delete option ' + n + '? This can\'t be undone.',
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }
  }

  return null
}

/**
 * The hint under a footer button (UX spec 4.4) that depends on who is e-mailed. The same copy as the confirms, kept apart so the
 * button and the confirm can never say different things.
 *
 *   propose, Tower Co, mail on    E-mails {Telco} every option in detail.
 *   propose, client, mail on      E-mails {Tower Co} a short note with the link.
 *   propose, mail off             Send E-mails is off for this RTA, so nobody is e-mailed.
 *   propose, Tower Co client      Moves the options to Client Review. No e-mail is sent.
 *   propose, no options at all    Add an option to propose.                              (ctx.noOptions)
 *   propose, no Draft option      Every option is declined. Add an option to propose.   (ctx.noDraft)
 *   propose, mail unknown         ''
 */
export function proposeHint (target, ctx) {
  ctx = ctx || {}

  if (ctx.noOptions === true) {
    return 'Add an option to propose.'
  }

  if (ctx.noDraft === true) {
    return 'Every option is declined. Add an option to propose.'
  }

  if (target === 'self') {
    return 'Moves the options to Client Review. No e-mail is sent.'
  }

  if (ctx.mailOff === true) {
    return 'Send E-mails is off for this RTA, so nobody is e-mailed.'
  }

  if (ctx.mailPossible !== true) {
    return ''
  }

  return target === 'towerco'
    ? 'E-mails ' + (ctx.towerCo || 'the Tower Co') + ' a short note with the link.'
    : 'E-mails ' + (ctx.telco || 'the client') + ' every option in detail.'
}
